import api from '@/services/utils/apiInstance.ts';
import { buildQuery } from '@/services/utils/request.ts';
import {
  InvestmentStatusKey,
  WithdrawalStatusKey
} from "@/enums/bank_enum.ts";

import type { PaginatedQuery } from '@/types/services/utils/request.type.ts';
import type { PaginatedResponse } from '@/types/services/utils/response.type.ts';
import type {
  InvestmentOperationDetailResponse,
  InvestmentOperationResponse,
  InvestmentOperationResponseApi,
  WithdrawalOperationDetailResponse,
  WithdrawalOperationResponse,
  WithdrawalOperationResponseApi
} from "@/types/services/bank/bank.type.ts";

class BankService {
  async getPaginatedInvestmentOperations(params: PaginatedQuery): Promise<PaginatedResponse<InvestmentOperationResponse>> {
    const query: string = buildQuery(params)
    const response = await api.get(`/bank/investment/paginated?${query.toString()}`)
    return {
      total: response.data.total,
      page: response.data.page,
      limit: response.data.limit,
      maxPages: response.data.max_pages,
      data: response.data.data.map((investment: InvestmentOperationResponseApi) => ({
        contractId: investment.contract_id,
        username: investment.username,
        email: investment.email,
        reference: investment.reference,
        investmentAmount: investment.investment_amount,
        createdAt: investment.created_at,
        status: investment.status
      }))
    }
  }

  async getInvestment(contractId: number): Promise<InvestmentOperationDetailResponse> {
    const response = await api.get(`/bank/investment/${contractId}`)
    return {
      contractId: response.data.contract_id,
      username: response.data.username,
      email: response.data.email,
      reference: response.data.reference,
      investmentAmount: response.data.investment_amount,
      createdAt: response.data.created_at,
      status: response.data.status,
      mandateNames: response.data.mandate_names
    }
  }

  async updateInvestmentStatus(contractId: number, status: InvestmentStatusKey): Promise<void> {
    await api.post(`/bank/investment/${contractId}`, {
      status: status
    });
  }

  async getPaginatedWithdrawalOperations(params: PaginatedQuery): Promise<PaginatedResponse<WithdrawalOperationResponse>> {
    const query: string = buildQuery(params)
    const response = await api.get(`/bank/withdrawal/paginated?${query.toString()}`)
    return {
      total: response.data.total,
      page: response.data.page,
      limit: response.data.limit,
      maxPages: response.data.max_pages,
      data: response.data.data.map((withdrawal: WithdrawalOperationResponseApi) => ({
        withdrawalId: withdrawal.withdrawal_id,
        username: withdrawal.username,
        email: withdrawal.email,
        mandateName: withdrawal.mandate_name,
        amount: withdrawal.amount,
        userRib: withdrawal.user_rib,
        createdAt: withdrawal.created_at,
        status: withdrawal.status
      }))
    }
  }

  async getWithdrawal(withdrawalId: number): Promise<WithdrawalOperationDetailResponse> {
    const response = await api.get(`/bank/withdrawal/${withdrawalId}`)
    return {
      withdrawalId: response.data.withdrawal_id,
      username: response.data.username,
      email: response.data.email,
      mandateName: response.data.mandate_name,
      amount: response.data.amount,
      userRib: response.data.user_rib,
      createdAt: response.data.created_at,
      status: response.data.status,
      bankName: response.data.bank_name,
      iban: response.data.iban,
      bic: response.data.bic,
      currency: response.data.currency,
      ownerName: response.data.owner_name
    }
  }

  async updateWithdrawalStatus(
    withdrawalId: number,
    status: WithdrawalStatusKey,
    reason: string | null = null
  ): Promise<void> {
    await api.post(`/bank/withdrawal/${withdrawalId}`, {
      status: status,
      reason: reason
    });
  }
}

export default new BankService();
